import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Checkbox,
    Button,
    Divider,
    CircularProgress
} from '@mui/material';
import Modal from 'components/common/Modal';
import { CloseOutlinedIcon, SearchOutlinedIcon } from 'icons/materialIcons';
import { useTeamMember } from 'team/contexts/teamMemberContext';
import { useTeam } from 'team/contexts/teamContext';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { getEmailSignature } from 'team/apis/teamEmailSignatureApi';
import PropTypes from 'prop-types';

const TeamEmailSignatureAssignMembersModal = ({ open, onClose, template, teamId, onSave }) => {
    const { getMembers, teamMembers, loading: membersLoading } = useTeamMember();
    const { currentTeam } = useTeam();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAssigned, setSelectedAssigned] = useState(new Set());
    const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [setAsDefault, setSetAsDefault] = useState(false);
    const [createTeamCohesion, setCreateTeamCohesion] = useState(false);
    const [allTeamMembersList, setAllTeamMembersList] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(template);

    // Fetch latest template data when modal opens to ensure we have updated assignedMembers
    useEffect(() => {
        const fetchLatestTemplate = async () => {
            if (!open || !teamId || !template?._id) return;
            
            try {
                const latestTemplate = await getEmailSignature(teamId, template._id);
                if (latestTemplate?.emailSignature) {
                    console.log('🔍 Fetched latest template with assignedMembers:', latestTemplate.emailSignature.assignedMembers);
                    setCurrentTemplate(latestTemplate.emailSignature);
                }
            } catch (error) {
                console.error('🔍 Error fetching latest template:', error);
                // Fallback to provided template
                setCurrentTemplate(template);
            }
        };
        
        fetchLatestTemplate();
    }, [open, teamId, template?._id]);

    // Update currentTemplate when template prop changes
    useEffect(() => {
        if (template) {
            setCurrentTemplate(template);
        }
    }, [template]);

    // Fetch team members when modal opens - same pattern as subteam
    useEffect(() => {
        const fetchMembers = async () => {
            if (!open || !teamId) return;
            
            setLoadingMembers(true);
            console.log('🔍 TeamEmailSignatureAssignMembersModal - Fetching members for teamId:', teamId);
            console.log('🔍 TeamEmailSignatureAssignMembersModal - Current template assignedMembers:', currentTemplate?.assignedMembers);
            try {
                // Use direct API call like subteam does
                const teamMembersResponse = await getTeamMembers(teamId, { status: 'active' });
                const teamMembers = teamMembersResponse?.members || [];
                console.log('🔍 TeamEmailSignatureAssignMembersModal - All team members:', teamMembers.length);
                setAllTeamMembersList(teamMembers);
                
                // Also update context for consistency
                await getMembers(teamId, { status: 'active' });
            } catch (error) {
                console.error('🔍 TeamEmailSignatureAssignMembersModal - Error fetching members:', error);
            } finally {
                setLoadingMembers(false);
            }
        };
        
        fetchMembers();
    }, [open, teamId, getMembers, currentTemplate]);

    // Helper functions - defined first so they can be used in useMemo hooks
    const getMemberId = (member) => {
        return member._id || member.userId || member.user?._id || member.user?.userId;
    };

    const getMemberEmail = (member) => {
        return member.user?.email || member.email || '';
    };

    const getMemberName = (member) => {
        return member.user?.fullName || member.fullName || 'Unknown';
    };

    const getMemberProfilePicture = (member) => {
        return member.user?.profilePictureUrl || member.profilePictureUrl;
    };

    // Helper to get card name (for assigned members - shows card name instead of member name)
    const getCardName = (member) => {
        return member.defaultCard?.cardName || 
               member.defaultCard?.contact_info?.fullName || 
               member.cardName ||
               member.contact_info?.fullName ||
               getMemberName(member);
    };

    // Filter members by search query
    const matchesSearch = (member) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const fullName = (member.user?.fullName || member.fullName || '').toLowerCase();
        const email = (member.user?.email || member.email || '').toLowerCase();
        return fullName.includes(query) || email.includes(query);
    };

    // Get assigned member IDs from template
    // assignedMembers are populated User objects with userId field (Firebase UID)
    const assignedUserIds = useMemo(() => {
        const templateToUse = currentTemplate || template;
        if (!templateToUse || !templateToUse.assignedMembers || !Array.isArray(templateToUse.assignedMembers)) {
            console.log('🔍 No assigned members in template:', {
                hasTemplate: !!templateToUse,
                hasAssignedMembers: !!(templateToUse && templateToUse.assignedMembers),
                assignedMembers: templateToUse?.assignedMembers
            });
            return new Set();
        }
        const userIds = new Set();
        templateToUse.assignedMembers.forEach(m => {
            if (!m) return;
            
            // If it's a populated User object, get the userId field (Firebase UID)
            if (typeof m === 'object' && m.userId) {
                userIds.add(m.userId.toString());
                console.log('🔍 Found assigned member userId:', m.userId, 'from user:', m.fullName || m.email);
            } 
            // If it's just an ObjectId string (shouldn't happen if populated correctly)
            else if (typeof m === 'string') {
                console.warn('🔍 Unpopulated assignedMember found (ObjectId string):', m);
                // Can't match this without looking up the User
            }
        });
        console.log('🔍 Assigned User IDs from template:', Array.from(userIds));
        return userIds;
    }, [currentTemplate, template]);

    // Split members - EXACTLY like subteam does it
    const assignedMembers = useMemo(() => {
        if (!Array.isArray(allTeamMembersList) || allTeamMembersList.length === 0) {
            return [];
        }
        return allTeamMembersList.filter(member => {
            if (!member) return false;
            const userId = member.userId?.toString() || member.user?._id?.toString() || member.user?.userId?.toString();
            return userId && assignedUserIds.has(userId);
        }).map(member => ({
            ...member,
            _memberId: member._id?.toString() || member.userId?.toString() || '',
            _userId: member.userId?.toString() || member.user?._id?.toString() || member.user?.userId?.toString()
        }));
    }, [allTeamMembersList, assignedUserIds]);

    const unassignedMembers = useMemo(() => {
        if (!Array.isArray(allTeamMembersList) || allTeamMembersList.length === 0) {
            return [];
        }
        return allTeamMembersList.filter(member => {
            if (!member) return false;
            const userId = member.userId?.toString() || member.user?._id?.toString() || member.user?.userId?.toString();
            return !userId || !assignedUserIds.has(userId);
        }).map(member => ({
            ...member,
            _memberId: member._id?.toString() || member.userId?.toString() || '',
            _userId: member.userId?.toString() || member.user?._id?.toString() || member.user?.userId?.toString()
        }));
    }, [allTeamMembersList, assignedUserIds]);

    // Handle select all for assigned members
    const handleSelectAllAssigned = () => {
        if (selectedAssigned.size === assignedMembers.length && assignedMembers.length > 0) {
            setSelectedAssigned(new Set());
        } else {
            const allIds = new Set(assignedMembers.map(m => m._memberId));
            setSelectedAssigned(allIds);
        }
    };

    // Handle select all for unassigned members
    const handleSelectAllUnassigned = () => {
        if (selectedUnassigned.size === unassignedMembers.length && unassignedMembers.length > 0) {
            setSelectedUnassigned(new Set());
        } else {
            const allIds = new Set(unassignedMembers.map(m => m._memberId));
            setSelectedUnassigned(allIds);
        }
    };

    // Handle individual member toggle
    const handleMemberToggle = (memberId, isAssigned) => {
        if (isAssigned) {
            const newSelected = new Set(selectedAssigned);
            if (newSelected.has(memberId)) {
                newSelected.delete(memberId);
            } else {
                newSelected.add(memberId);
            }
            setSelectedAssigned(newSelected);
        } else {
            const newSelected = new Set(selectedUnassigned);
            if (newSelected.has(memberId)) {
                newSelected.delete(memberId);
            } else {
                newSelected.add(memberId);
            }
            setSelectedUnassigned(newSelected);
        }
    };

    // Handle save
    const handleSave = async () => {
        const templateToUse = currentTemplate || template;
        if (!templateToUse || !onSave) return;

        setIsSubmitting(true);
        try {
            // Get User IDs to assign and unassign (not TeamMember IDs)
            // selectedUnassigned and selectedAssigned contain _memberId (TeamMember ID)
            // We need to convert them to User IDs (userId)
            const allMembersForLookup = Array.isArray(allTeamMembersList) ? allTeamMembersList : [];
            const toAssign = Array.from(selectedUnassigned).map(memberId => {
                const member = allMembersForLookup.find(m => getMemberId(m).toString() === memberId);
                return member?.userId?.toString() || member?.user?._id?.toString() || memberId;
            }).filter(Boolean);
            
            const toUnassign = Array.from(selectedAssigned).map(memberId => {
                const member = allMembersForLookup.find(m => getMemberId(m).toString() === memberId);
                return member?.userId?.toString() || member?.user?._id?.toString() || memberId;
            }).filter(Boolean);

            console.log('🔍 TeamEmailSignatureAssignMembersModal - Saving assignments:', {
                toAssign,
                toUnassign,
                setAsDefault
            });

            await onSave({
                templateId: templateToUse._id,
                assignMemberIds: toAssign,
                unassignMemberIds: toUnassign,
                setAsDefault
            });

            // Reset selections
            setSelectedAssigned(new Set());
            setSelectedUnassigned(new Set());
            setSetAsDefault(false);
            onClose();
        } catch (error) {
            console.error('Error saving member assignments:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isAllSelectedAssigned = assignedMembers && assignedMembers.length > 0 && selectedAssigned.size === assignedMembers.length;
    const isAllSelectedUnassigned = unassignedMembers && unassignedMembers.length > 0 && selectedUnassigned.size === unassignedMembers.length;

    return (
        <Modal open={open} onClose={onClose} width="60%" maxWidth={900} height="85%">
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                        Assign Members to {(currentTemplate || template)?.signatureName || 'Email Signature'}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#666' }}>
                        <CloseOutlinedIcon />
                    </IconButton>
                </Box>

                {/* Search */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ color: '#999' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '1.5rem',
                                backgroundColor: '#f7f7f7',
                            },
                        }}
                    />
                </Box>

                {/* Content - Two Columns */}
                {(membersLoading || loadingMembers) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', gap: 4, flex: 1, overflow: 'hidden' }}>
                        {/* Assigned Section */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#4f4f4f' }}>
                                    Assigned ({assignedMembers?.length || 0})
                                </Typography>
                                {assignedMembers && assignedMembers.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={isAllSelectedAssigned}
                                            onChange={handleSelectAllAssigned}
                                            sx={{
                                                color: '#e0e0e0',
                                                padding: '0',
                                                marginRight: '0.5rem',
                                                '&.Mui-checked': {
                                                    color: '#000',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '0.5rem',
                                                },
                                            }}
                                        />
                                        <Typography
                                            onClick={handleSelectAllAssigned}
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                color: '#4f4f4f',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Select All
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Divider sx={{ borderColor: '#e0e0e0', mb: 2 }} />
                            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                {(() => {
                                    if (!assignedMembers || !Array.isArray(assignedMembers)) {
                                        return (
                                            <Typography sx={{ color: '#9d9d9d', fontSize: '0.875rem', padding: '20px 0' }}>
                                                No assigned members
                                            </Typography>
                                        );
                                    }
                                    const filteredAssigned = assignedMembers.filter(member => matchesSearch(member));
                                    if (!filteredAssigned || filteredAssigned.length === 0) {
                                        return (
                                            <Typography sx={{ color: '#9d9d9d', fontSize: '0.875rem', padding: '20px 0' }}>
                                                No assigned members
                                            </Typography>
                                        );
                                    }
                                    return filteredAssigned.map((member) => {
                                        const memberId = member._memberId;
                                        const isSelected = selectedAssigned.has(memberId);
                                        const cardName = getCardName(member);
                                        const email = getMemberEmail(member);

                                        return (
                                            <Box
                                                key={memberId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px 0',
                                                    borderBottom: '1px solid #f0f0f0'
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleMemberToggle(memberId, true)}
                                                    sx={{
                                                        color: '#e0e0e0',
                                                        padding: '0',
                                                        marginRight: '12px',
                                                        '&.Mui-checked': {
                                                            color: '#000',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            borderRadius: '0.5rem',
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                            color: '#4f4f4f'
                                                        }}
                                                    >
                                                        {cardName}
                                                    </Typography>
                                                    {email && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                color: '#9d9d9d'
                                                            }}
                                                        >
                                                            {email}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    });
                                })()}
                            </Box>
                        </Box>

                        {/* Unassigned Section */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#4f4f4f' }}>
                                    Unassigned ({Array.isArray(unassignedMembers) ? unassignedMembers.filter(m => matchesSearch(m)).length : 0})
                                </Typography>
                                {Array.isArray(unassignedMembers) && unassignedMembers.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={isAllSelectedUnassigned}
                                            onChange={handleSelectAllUnassigned}
                                            sx={{
                                                color: '#e0e0e0',
                                                padding: '0',
                                                marginRight: '0.5rem',
                                                '&.Mui-checked': {
                                                    color: '#000',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '0.5rem',
                                                },
                                            }}
                                        />
                                        <Typography
                                            onClick={handleSelectAllUnassigned}
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                color: '#4f4f4f',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Select All
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            <Divider sx={{ borderColor: '#e0e0e0', mb: 2 }} />
                            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                {(() => {
                                    if (!Array.isArray(unassignedMembers) || unassignedMembers.length === 0) {
                                        return (
                                            <Typography sx={{ color: '#9d9d9d', fontSize: '0.875rem', padding: '20px 0' }}>
                                                {allTeamMembersList.length === 0 ? 'No team members found' : 'No unassigned members'}
                                            </Typography>
                                        );
                                    }
                                    
                                    const filteredUnassigned = unassignedMembers.filter(member => matchesSearch(member));
                                    
                                    if (filteredUnassigned.length === 0) {
                                        return (
                                            <Typography sx={{ color: '#9d9d9d', fontSize: '0.875rem', padding: '20px 0' }}>
                                                No members match your search
                                            </Typography>
                                        );
                                    }
                                    
                                    return filteredUnassigned.map((member) => {
                                                const memberId = member._memberId;
                                                const isSelected = selectedUnassigned.has(memberId);
                                                const fullName = getMemberName(member);
                                                const email = getMemberEmail(member);

                                        return (
                                            <Box
                                                key={memberId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    padding: '12px 0',
                                                    borderBottom: '1px solid #f0f0f0'
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleMemberToggle(memberId, false)}
                                                    sx={{
                                                        color: '#e0e0e0',
                                                        padding: '0',
                                                        marginRight: '12px',
                                                        '&.Mui-checked': {
                                                            color: '#000',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            borderRadius: '0.5rem',
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                            color: '#4f4f4f'
                                                        }}
                                                    >
                                                        {fullName}
                                                    </Typography>
                                                    {email && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                color: '#9d9d9d'
                                                            }}
                                                        >
                                                            {email}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    });
                                })()}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Footer */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                    {/* Left side - Create team cohesion checkbox */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={createTeamCohesion}
                            onChange={(e) => setCreateTeamCohesion(e.target.checked)}
                            sx={{
                                color: '#e0e0e0',
                                padding: '0',
                                marginRight: '0.5rem',
                                '&.Mui-checked': {
                                    color: '#000',
                                },
                                '& .MuiSvgIcon-root': {
                                    borderRadius: '0.5rem',
                                },
                            }}
                        />
                        <Typography 
                            sx={{ 
                                fontSize: '0.875rem', 
                                color: '#4f4f4f',
                                cursor: 'pointer'
                            }}
                            onClick={() => setCreateTeamCohesion(!createTeamCohesion)}
                        >
                            Create team cohesion
                        </Typography>
                    </Box>
                    
                    {/* Right side - Set as default checkbox and button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Checkbox
                                checked={setAsDefault}
                                onChange={(e) => setSetAsDefault(e.target.checked)}
                                sx={{
                                    color: '#e0e0e0',
                                    padding: '0',
                                    marginRight: '0.5rem',
                                    '&.Mui-checked': {
                                        color: '#000',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        borderRadius: '0.5rem',
                                    },
                                }}
                            />
                            <Typography 
                                sx={{ 
                                    fontSize: '0.875rem', 
                                    color: '#4f4f4f',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSetAsDefault(!setAsDefault)}
                            >
                                Set as default email signature for those assigned
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isSubmitting || (selectedAssigned.size === 0 && selectedUnassigned.size === 0)}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'none',
                                padding: '10px 24px',
                                fontWeight: 600,
                                minWidth: '180px',
                                '&:hover': {
                                    backgroundColor: '#333',
                                },
                                '&:disabled': {
                                    backgroundColor: '#e0e0e0',
                                    color: '#999'
                                }
                            }}
                        >
                            {isSubmitting 
                                ? 'Saving...' 
                                : (selectedUnassigned.size === 0 && selectedAssigned.size === 0)
                                    ? 'Waiting for action'
                                    : (selectedUnassigned.size > 0)
                                        ? `Assign ${selectedUnassigned.size} member${selectedUnassigned.size === 1 ? '' : 's'}`
                                        : `Unassign ${selectedAssigned.size} member${selectedAssigned.size === 1 ? '' : 's'}`
                            }
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};

TeamEmailSignatureAssignMembersModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    template: PropTypes.object,
    teamId: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired
};

export default TeamEmailSignatureAssignMembersModal;

